import { useRef, useState } from "react";
import { SVG } from "../../../../../../constants/assetLists";
import { useNavigate } from "react-router";
import { DaySummaryGroup, DaySummaryItem, DaySummaryLevel } from "../types";

type ItemCategory =
  | "forms"
  | "tasks"
  | "regulations"
  | "announcement"
  | "tutorials";

type SummaryCategory = (typeof categoryOrder)[number];

export type SummaryItem = {
  label: string;
  count?: number;
  availableFrom?: string;
  category?: "forms" | "tasks" | "regulations" | "tutorials";
  summaryCategory?: SummaryCategory;
  titles?: {
    id: string;
    title: string;
    category: ItemCategory;
  }[];
};

export type SummarySection = {
  title: string;
  color: "pink" | "yellow" | "blue";
  items: SummaryItem[];
};

export type SummaryCardData = {
  id: string;
  name: string;
  logo?: {
    url?: string;
    type?: string;
  } | null;
  level?: DaySummaryLevel;
  clientId?: string | null;
  branchId?: string | null;

  counters?: {
    label: string;
    value: number;
    color: "pink" | "yellow" | "blue";
  }[];

  sections?: SummarySection[];

  overDueCount?: number;
  currentCount?: number;
  comingSoonCount?: number;

  overDue?: DaySummaryGroup;
  currentState?: DaySummaryGroup;
  comingSoon?: DaySummaryGroup;
};

type Props = {
  data: SummaryCardData;
  onClose: () => void;
};

const sectionColors = {
  pink: "bg-pastelPink",
  yellow: "bg-pastelYellow",
  blue: "bg-pastelBlue",
};

const statusSections = [
  {
    key: "overDue",
    title: "פג תוקף",
    color: "pink",
  },
  {
    key: "currentState",
    title: "מצב נוכחי",
    color: "yellow",
  },
  {
    key: "comingSoon",
    title: "בקרוב",
    color: "blue",
  },
] as const;

const categoryLabels = {
  forms: "טופס",
  tasks: "משימות",
  regulations: "רגולציה",
  tutorials: "הדרכה",
  announcementsAndTasks: "משימות והודעות",
  unviewedAnnouncements: "הודעות שלא נצפו",
} as const;

const categoryOrder = [
  "forms",
  "tasks",
  "announcementsAndTasks",
  "unviewedAnnouncements",
  "tutorials",
  "regulations",
] as const;

function getRawItems(
  group: DaySummaryGroup | undefined,
  category: (typeof categoryOrder)[number],
) {
  return group?.[category] || [];
}

function getItemId(item: DaySummaryItem) {
  return item.announcementId || item._id;
}

function getItemTitle(item: DaySummaryItem) {
  return (
    item.taskTitle ??
    item.announcementTitle ??
    item.formTitle ??
    item.title ??
    ""
  );
}

function getLogoUrl(logo: SummaryCardData["logo"]) {
  return logo?.url;
}

function buildSections(data: SummaryCardData): SummarySection[] {
  return statusSections
    .map((status) => {
      const group = data[status.key];
      const items: SummaryItem[] = categoryOrder.flatMap(
        (category): SummaryItem[] => {
          const categoryItems = getRawItems(group, category);

          if (categoryItems.length === 0) {
            return [];
          }

          return [
            {
              count: categoryItems.length,
              label: categoryLabels[category],
              summaryCategory: category,
              category:
                category === "forms"
                  ? "forms"
                  : category === "tutorials"
                    ? "tutorials"
                    : category === "regulations"
                      ? "regulations"
                      : "tasks",

              titles: categoryItems.map((item) => {
                const itemCategory =
                  category === "forms"
                    ? "forms"
                    : category === "tutorials"
                      ? "tutorials"
                      : category === "regulations"
                        ? "regulations"
                        : category === "announcementsAndTasks" ||
                            category === "unviewedAnnouncements"
                          ? "announcement"
                          : "tasks";

                return {
                  id: getItemId(item),
                  title: getItemTitle(item),
                  category: itemCategory,
                };
              }),
            },
          ];
        },
      );

      return {
        title: status.title,
        color: status.color,
        items,
      };
    })
    .filter((section) => section.items.length > 0);
}

function getSectionItems(section: SummarySection) {
  return { items: section.items };
}

function getUniqueTitleIds(item: SummaryItem) {
  return Array.from(
    new Set(item.titles?.map((title) => title.id).filter(Boolean) ?? []),
  );
}

function getDaySummaryClearLabel(
  sectionTitle: string,
  category?: SummaryCategory,
) {
  const labelByCategoryAndSection: Partial<
    Record<SummaryCategory, Record<string, string>>
  > = {
    forms: {
      "פג תוקף": "ביטול פילטר טפסים פתוחים",
      "מצב נוכחי": "ביטול פילטר טפסים חדשים",
      בקרוב: "ביטול פילטר טפסים בקרוב",
    },
    tasks: {
      "פג תוקף": "ביטול פילטר משימות פתוחות",
      "מצב נוכחי": "ביטול פילטר משימות חדשות",
      בקרוב: "ביטול פילטר משימות בקרוב",
    },
    announcementsAndTasks: {
      "פג תוקף": "ביטול פילטר משימות והודעות פתוחות",
      "מצב נוכחי": "ביטול פילטר משימות והודעות חדשות",
      בקרוב: "ביטול פילטר משימות והודעות בקרוב",
    },
    unviewedAnnouncements: {
      "פג תוקף": "ביטול פילטר הודעות שלא נצפו פתוחות",
      "מצב נוכחי": "ביטול פילטר הודעות שלא נצפו חדשות",
      בקרוב: "ביטול פילטר הודעות שלא נצפו בקרוב",
    },
    tutorials: {
      "פג תוקף": "ביטול פילטר הדרכות פתוחות",
      "מצב נוכחי": "ביטול פילטר הדרכות חדשות",
      בקרוב: "ביטול פילטר הדרכות בקרוב",
    },
    regulations: {
      "פג תוקף": "ביטול פילטר רגולציות פתוחות",
      "מצב נוכחי": "ביטול פילטר רגולציות חדשות",
      בקרוב: "ביטול פילטר רגולציות בקרוב",
    },
  };

  return category
    ? (labelByCategoryAndSection[category]?.[sectionTitle] ??
        "ביטול סינון סיכום")
    : "ביטול סינון סיכום";
}

function SummaryCard({ data, onClose }: Props) {
  const [isCardOpen, setIsCardOpen] = useState(true);
  const cardRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();

  const sections = buildSections(data);
  const logoUrl = getLogoUrl(data.logo);

  const handleLinkClick = (titleItem: {
    id: string;
    title: string;
    category: ItemCategory;
  }) => {
    onClose();
    switch (titleItem.category) {
      case "forms":
        navigate("/forms", {
          state: {
            openFormId: titleItem.id,
          },
        });
        break;

      case "tutorials":
      case "announcement":
      case "tasks":
      case "regulations":
        navigate(`/operations`, {
          state: { announcementId: titleItem.id },
        });
        break;
    }
  };

  const handlePageClick = (item: SummaryItem, sectionTitle: string) => {
    onClose();
    const category = item.category ?? "announcement";
    const ids = getUniqueTitleIds(item);
    const clearLabel = getDaySummaryClearLabel(
      sectionTitle,
      item.summaryCategory,
    );

    switch (category) {
      case "forms":
        navigate("/forms", {
          state: {
            daySummaryFormIds: ids,
            daySummaryCategory: category,
            daySummaryLabel: item.label,
            daySummaryClearLabel: clearLabel,
            daySummaryClientId:
              data.level === "client" ? data.id : data.clientId,
            daySummaryBranchId:
              data.level === "branch" ? data.id : data.branchId,
            daySummarySubBranchId:
              data.level === "subBranch" ? data.id : undefined,
          },
        });
        break;

      case "tutorials":
      case "regulations":
      case "tasks":
      case "announcement":
        navigate("/operations", {
          state: {
            daySummaryAnnouncementIds: ids,
            daySummaryCategory: category,
            daySummaryLabel: item.label,
            daySummaryClearLabel: clearLabel,
            daySummaryClientId: data.clientId,
            daySummaryBranchId: data.branchId,
          },
        });
        break;
    }
  };

  const toggleCardOpen = () => {
    const shouldOpen = !isCardOpen;

    setIsCardOpen(shouldOpen);

    if (shouldOpen) {
      requestAnimationFrame(() => {
        cardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
  };

  return (
    <article
      ref={cardRef}
      className="min-w-0 rounded-lg border border-gray70 bg-white p-3"
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {sections?.length > 0 && (
            <button className="w-[14px] h-[14px]" onClick={toggleCardOpen}>
              <img
                src={isCardOpen ? SVG.expandIcon : SVG.lessThanIcon}
                alt="toggle filter"
                className="w-full h-full object-contain"
              />
            </button>
          )}
          <span className="text-lg font-bold text-darkerGray">{data.name}</span>
        </div>
        <div
          className={`w-[120px] h-[45px] ${logoUrl ? "" : "relative w-[68px] h-[42px] rounded-md border border-gray90 bg-gray90 flex items-center justify-center overflow-hidden py-1"}`}
        >
          <img
            src={logoUrl || SVG.imgPlaceholderWhiteIcon}
            alt="logo"
            className="w-full h-full object-contain"
          />
        </div>
      </header>

      <div
        className={`flex justify-start gap-2 ${isCardOpen && sections.length && "mb-3 border-b border-b-gray70 pb-2"}`}
      >
        {[
          {
            label: "פג תוקף",
            value: data.overDueCount ?? 0,
            color: "pink" as const,
          },
          {
            label: "מצב נוכחי",
            value: data.currentCount ?? 0,
            color: "yellow" as const,
          },
          {
            label: "בקרוב",
            value: data.comingSoonCount ?? 0,
            color: "blue" as const,
          },
        ].map((counter) => (
          <div
            key={counter.label}
            className="flex w-12 flex-col items-center gap-1 mr-4"
          >
            <span
              className={`flex w-[48px] h-[32px] items-center justify-center rounded-[4px] ${
                sectionColors[counter.color]
              } font-semibold`}
            >
              {counter.value}
            </span>

            <span className="whitespace-nowrap text-sm font-normal text-darkerGray">
              {counter.label}
            </span>
          </div>
        ))}
      </div>

      {isCardOpen && (
        <div className="space-y-2">
          {sections.map((section) => {
            const { items } = getSectionItems(section);

            return (
              <section key={section.title}>
                <div
                  className={`m-0 flex w-full items-center justify-between rounded-[4px] px-2 py-1 text-right text-base font-bold text-darkerGray ${
                    sectionColors[section.color]
                  }`}
                >
                  <span className="text-xl font-medium">{section.title}</span>
                </div>

                <div className="divide-y divide-gray70">
                  {items.map((item, index) => {
                    const titlesLength = item.titles?.length ?? 0;
                    const overdueItemsLength =
                      titlesLength >= 3 ? titlesLength : 0;

                    const showDetails =
                      section.title === "פג תוקף" && !overdueItemsLength;
                    const showInLine =
                      showDetails &&
                      item.titles?.length === 1 &&
                      item.titles[0]?.category !== "tasks";

                    return (
                      <div
                        key={`${item.label}-${index}`}
                        className="flex min-h-8 items-start justify-start gap-1 py-1 text-sm text-darkerGray"
                      >
                        {item.count !== undefined && (
                          <div
                            className={`flex gap-1 ${
                              showInLine ? "flex-row items-start" : "flex-col"
                            }`}
                          >
                            {showDetails ? (
                              <span className="text-xl font-normal flex-shrink-0 text-darkerGray">
                                {item.count} {item.label}
                              </span>
                            ) : (
                              <p className="text-xl font-normal text-darkerGray">
                                <span
                                  className="text-linkBlue underline text-xl font-bold cursor-pointer"
                                  onClick={() =>
                                    handlePageClick(item, section.title)
                                  }
                                >
                                  {item.count}
                                </span>{" "}
                                {item.label}
                              </p>
                            )}

                            {showDetails && (
                              <div
                                className={`flex gap-1 ${
                                  showInLine
                                    ? "flex-row items-center"
                                    : "flex-col"
                                }`}
                              >
                                {item.titles?.map((titleItem) => (
                                  <div
                                    key={`${titleItem.id}-${titleItem.title}`}
                                    className="flex items-start gap-1"
                                  >
                                    {titleItem.category === "tasks" && (
                                      <img
                                        src={SVG.enterArrowIcon}
                                        alt="logo"
                                        className="w-[24px] h-[24px] object-contain"
                                      />
                                    )}
                                    <button
                                      className="text-linkBlue underline text-xl text-end"
                                      onClick={() => {
                                        handleLinkClick(titleItem);
                                      }}
                                    >
                                      {titleItem.title}
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </article>
  );
}

export default SummaryCard;
